import React, { useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, InputGroup, InputGroupText, Alert } from 'reactstrap';
import { parseCurrencyToFloat, displayDecimal } from '../../../../_helper/regexHandler';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';
import { ACTIONS_FOR_RULING, APPRAISAL_FEE_STATUSES, RULING_RESULTS, RULING_TYPES } from '../../../../_helper/schemaAllowedValues';

const RulingDataModal = ({
    apiEndpoint,
    isOpen,
    toggle,
    mode,
    selectedRuling,
    refreshData,
    arbitrationId
}) => {
    const initialRulingData = {
        id: '',
        arbitration: [],
        expertWitnessReportInformation: [],
        arrivalDate: '',
        rulingNumber: '',
        rulingResult: '',
        rulingType: '',
        appraisalFeeStatus: '',
        principalClaim: '',
        acceptedAppraisalFee: '',
        powerOfAttorneyFee: '',
        actionForRuling: '',
        objectionFee: '',
        objectionCaseNumber: '',
        objectingParty: '',
        opposingPowerOfAttorneyFee: '',
        willPayCounterPowerOfAttorneyFee: null,
        cpFullName: '',
        cpIban: '',
        cpBank: '',
        cpEmail: '',
        comments: ''
    };

    const customHandlers = useMemo(() => ({
        willPayCounterPowerOfAttorneyFee: (value, prev) => ({
            ...prev,
            willPayCounterPowerOfAttorneyFee: value === 'true',
        }),
    }), []);

    const validationSchema = useMemo(() => ({
        rulingNumber: { required: true, message: 'Karar numarası gereklidir.' },
        arrivalDate: { required: true, message: 'Geliş tarihi gereklidir.' },
        rulingResult: { required: true, message: 'Karar sonucu gereklidir.' },
        rulingType: { required: true, message: 'Karar türü gereklidir.' },
        appraisalFeeStatus: { required: true, message: 'Ekspertiz ücreti durumu gereklidir.' },
        actionForRuling: { required: true, message: 'Karar için eylem gereklidir.' },
        principalClaim: { required: true, message: 'Asıl alacak gereklidir.' },
        acceptedAppraisalFee: { required: true, message: 'Kabul edilen ekspertiz ücreti gereklidir.' },
        powerOfAttorneyFee: { required: true, message: 'Vekalet ücreti gereklidir.' },
        willPayCounterPowerOfAttorneyFee: { required: true, message: 'Karşı vekalet ücreti ödenecek mi seçimi gereklidir.' },
        comments: { required: true, message: 'Yorum gereklidir.' },
    }), []);

    const {
        formData: rulingData,
        errors: validationErrors,
        handleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedRuling,
        initialData: initialRulingData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const commentText = selectedItem?.comments && Array.isArray(selectedItem.comments) && selectedItem.comments.length > 0
                ? selectedItem.comments[0].comment || ''
                : '';

            return {
                ...initialData,
                id: selectedItem?.id || '',
                arbitration: selectedItem?.arbitration || [arbitrationId],
                expertWitnessReportInformation: selectedItem?.expertWitnessReportInformation || [],
                arrivalDate: selectedItem?.arrivalDate ? selectedItem.arrivalDate.split('T')[0] : '',
                rulingNumber: selectedItem?.rulingNumber || '',
                rulingResult: selectedItem?.rulingResult || '',
                rulingType: selectedItem?.rulingType || '',
                appraisalFeeStatus: selectedItem?.appraisalFeeStatus || '',
                principalClaim: selectedItem?.principalClaim?.toString() || '',
                acceptedAppraisalFee: selectedItem?.acceptedAppraisalFee?.toString() || '',
                powerOfAttorneyFee: selectedItem?.powerOfAttorneyFee?.toString() || '',
                actionForRuling: selectedItem?.actionForRuling || '',
                objectionFee: selectedItem?.objectionFee?.toString() || '',
                objectionCaseNumber: selectedItem?.objectionCaseNumber || '',
                objectingParty: selectedItem?.objectingParty || '',
                opposingPowerOfAttorneyFee: selectedItem?.opposingPowerOfAttorneyFee?.toString() || '',
                willPayCounterPowerOfAttorneyFee: selectedItem?.willPayCounterPowerOfAttorneyFee ?? null,
                cpFullName: selectedItem?.cpFullName || '',
                cpIban: selectedItem?.cpIban || '',
                cpBank: selectedItem?.cpBank || '',
                cpEmail: selectedItem?.cpEmail || '',
                comments: commentText,
            };
        },
        normalizeInput: true,
        customHandlers,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            refreshData,
            componentName: 'Karar',
            buildPayload: (data) => ({
                ...data,
                arbitration: [arbitrationId],
                principalClaim: parseCurrencyToFloat(data.principalClaim),
                acceptedAppraisalFee: parseCurrencyToFloat(data.acceptedAppraisalFee),
                powerOfAttorneyFee: parseCurrencyToFloat(data.powerOfAttorneyFee),
                objectionFee: parseCurrencyToFloat(data.objectionFee),
                opposingPowerOfAttorneyFee: parseCurrencyToFloat(data.opposingPowerOfAttorneyFee),
                cpFullName: data.cpFullName || null,
                cpIban: data.cpIban || null,
                cpBank: data.cpBank || null,
                cpEmail: data.cpEmail || null,
                comments: data.comments ? [{ comment: data.comments }] : [],
                objectionExpenses: [],
                counterPartyPoaExpenses: [],
            }),
        },
    });

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="xl">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'Karar Ekle' : 'Karar Düzenle'}
            </ModalHeader>
            <Form>
                <ModalBody>
                    {Object.keys(validationErrors).length > 0 && (
                        <Alert color="danger">
                            <ul className="mb-0">
                                {Object.values(validationErrors).filter(Boolean).map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label for="arrivalDate">
                                    Geliş Tarihi <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="date"
                                    name="arrivalDate"
                                    id="arrivalDate"
                                    value={rulingData.arrivalDate}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.arrivalDate}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="rulingNumber">
                                    Karar Numarası <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    name="rulingNumber"
                                    id="rulingNumber"
                                    value={rulingData.rulingNumber}
                                    onChange={handleInputChange}
                                    placeholder="Karar numarasını giriniz"
                                    invalid={!!validationErrors.rulingNumber}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="rulingResult">Karar Sonucu <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="rulingResult"
                                    id="rulingResult"
                                    value={rulingData.rulingResult}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.rulingResult}
                                >
                                    <option value="">Karar sonucu seçiniz</option>
                                    {RULING_RESULTS.map(result => (
                                        <option key={result} value={result}>{result}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label for="rulingType">Karar Türü <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="rulingType"
                                    id="rulingType"
                                    value={rulingData.rulingType}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.rulingType}
                                >
                                    <option value="">Karar türü seçiniz</option>
                                    {RULING_TYPES.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="appraisalFeeStatus">Ekspertiz Ücreti Durumu <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="appraisalFeeStatus"
                                    id="appraisalFeeStatus"
                                    value={rulingData.appraisalFeeStatus}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.appraisalFeeStatus}
                                >
                                    <option value="">Durum seçiniz</option>
                                    {APPRAISAL_FEE_STATUSES.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="actionForRuling">Karar İçin Eylem <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="actionForRuling"
                                    id="actionForRuling"
                                    value={rulingData.actionForRuling}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.actionForRuling}
                                >
                                    <option value="">Eylem seçiniz</option>
                                    {ACTIONS_FOR_RULING.map(action => (
                                        <option key={action} value={action}>{action}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    {rulingData.rulingType === 'İtiraz' && (
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label for="objectionFee">İtiraz Ücreti</Label>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            name="objectionFee"
                                            id="objectionFee"
                                            value={displayDecimal(rulingData.objectionFee)}
                                            onChange={handleInputChange}
                                            placeholder="Örn: 1000,50"
                                            style={{
                                                textAlign: 'right',
                                                borderTopRightRadius: 0,
                                                borderBottomRightRadius: 0
                                            }}
                                        />
                                        <InputGroupText
                                            style={{
                                                borderTopLeftRadius: 0,
                                                borderBottomLeftRadius: 0,
                                                backgroundColor: '#f8f9fa',
                                                borderLeft: 'none'
                                            }}
                                        >
                                            ₺
                                        </InputGroupText>
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label for="objectionCaseNumber">İtiraz Dava Numarası</Label>
                                    <Input
                                        type="text"
                                        name="objectionCaseNumber"
                                        id="objectionCaseNumber"
                                        value={rulingData.objectionCaseNumber}
                                        onChange={handleInputChange}
                                        placeholder="İtiraz dava numarasını giriniz"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label for="objectingParty">İtiraz Eden Taraf</Label>
                                    <Input
                                        type="select"
                                        name="objectingParty"
                                        id="objectingParty"
                                        value={rulingData.objectingParty}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">İtiraz eden tarafı seçiniz</option>
                                        <option value="Sigorta Şirketi">Sigorta Şirketi</option>
                                        <option value="Biz">Biz</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label for="principalClaim">Asıl Alacak <span className="text-danger">*</span></Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="principalClaim"
                                            id="principalClaim"
                                            value={displayDecimal(rulingData.principalClaim)}
                                            onChange={handleInputChange}
                                            placeholder="Örn: 1000,50"
                                            invalid={!!validationErrors.principalClaim}
                                            style={{
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText
                                        style={{
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        ₺
                                    </InputGroupText>
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="acceptedAppraisalFee">Kabul Edilen Ekspertiz Ücreti <span className="text-danger">*</span></Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="acceptedAppraisalFee"
                                            id="acceptedAppraisalFee"
                                            value={displayDecimal(rulingData.acceptedAppraisalFee)}
                                            onChange={handleInputChange}
                                            placeholder="Örn: 1000,50"
                                            invalid={!!validationErrors.acceptedAppraisalFee}
                                            style={{
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText
                                        style={{
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        ₺
                                    </InputGroupText>
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="powerOfAttorneyFee">Vekalet Ücreti <span className="text-danger">*</span></Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="powerOfAttorneyFee"
                                            id="powerOfAttorneyFee"
                                            value={displayDecimal(rulingData.powerOfAttorneyFee)}
                                            onChange={handleInputChange}
                                            placeholder="Örn: 1000,50"
                                            invalid={!!validationErrors.powerOfAttorneyFee}
                                            style={{
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText
                                        style={{
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        ₺
                                    </InputGroupText>
                                </InputGroup>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label className="text-nowrap mb-2">Karşı Vekalet Ücreti Ödenecek mi? <span className="text-danger">*</span></Label>
                                <div className="d-flex mt-2">
                                    <div className="radio radio-primary me-3 d-flex align-items-center">
                                        <Input
                                            name="willPayCounterPowerOfAttorneyFee"
                                            type="radio"
                                            value="true"
                                            id="willPayYes"
                                            checked={rulingData.willPayCounterPowerOfAttorneyFee === true}
                                            onChange={handleInputChange}
                                        />
                                        <Label check for="willPayYes" className="ms-1 mb-0">Evet</Label>
                                    </div>
                                    <div className="radio radio-primary d-flex align-items-center">
                                        <Input
                                            name="willPayCounterPowerOfAttorneyFee"
                                            type="radio"
                                            value="false"
                                            id="willPayNo"
                                            checked={rulingData.willPayCounterPowerOfAttorneyFee === false}
                                            onChange={handleInputChange}
                                        />
                                        <Label check for="willPayNo" className="ms-1 mb-0">Hayır</Label>
                                    </div>
                                </div>
                                {validationErrors.willPayCounterPowerOfAttorneyFee && (
                                    <small className="text-danger">{validationErrors.willPayCounterPowerOfAttorneyFee}</small>
                                )}
                            </FormGroup>
                        </Col>
                        {rulingData.willPayCounterPowerOfAttorneyFee === true && (
                            <Col md="6">
                                <FormGroup>
                                    <Label for="opposingPowerOfAttorneyFee">Karşı Vekalet Ücreti</Label>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            name="opposingPowerOfAttorneyFee"
                                            id="opposingPowerOfAttorneyFee"
                                            value={displayDecimal(rulingData.opposingPowerOfAttorneyFee)}
                                            onChange={handleInputChange}
                                            placeholder="Örn: 1000,50"
                                            style={{
                                                textAlign: 'right',
                                                borderTopRightRadius: 0,
                                                borderBottomRightRadius: 0
                                            }}
                                        />
                                        <InputGroupText
                                            style={{
                                                borderTopLeftRadius: 0,
                                                borderBottomLeftRadius: 0,
                                                backgroundColor: '#f8f9fa',
                                                borderLeft: 'none'
                                            }}
                                        >
                                            ₺
                                        </InputGroupText>
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                        )}
                    </Row>

                    {rulingData.willPayCounterPowerOfAttorneyFee === true && (
                        <>
                            <Row>
                                <Col md="12">
                                    <h6 className="mb-3 text-primary">Karşı Taraf Bilgileri</h6>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="cpFullName">Karşı Taraf Ad Soyad</Label>
                                        <Input
                                            type="text"
                                            name="cpFullName"
                                            id="cpFullName"
                                            value={rulingData.cpFullName}
                                            onChange={handleInputChange}
                                            placeholder="Ad soyad giriniz"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="cpEmail">Karşı Taraf E-posta</Label>
                                        <Input
                                            type="email"
                                            name="cpEmail"
                                            id="cpEmail"
                                            value={rulingData.cpEmail}
                                            onChange={handleInputChange}
                                            placeholder="ornek@sirket.com"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="cpBank">Karşı Taraf Banka</Label>
                                        <Input
                                            type="text"
                                            name="cpBank"
                                            id="cpBank"
                                            value={rulingData.cpBank}
                                            onChange={handleInputChange}
                                            placeholder="Banka adı giriniz"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="cpIban">Karşı Taraf IBAN</Label>
                                        <Input
                                            type="text"
                                            name="cpIban"
                                            id="cpIban"
                                            value={rulingData.cpIban}
                                            onChange={handleInputChange}
                                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                                            maxLength="34"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </>
                    )}
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label for="comments">Yorum <span className="text-danger">*</span></Label>
                                <Input
                                    type="textarea"
                                    name="comments"
                                    id="comments"
                                    rows="3"
                                    value={rulingData.comments}
                                    onChange={handleInputChange}
                                    placeholder="Karar ile ilgili yorumlarınızı giriniz..."
                                    invalid={!!validationErrors.comments}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        İptal
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default RulingDataModal;
